import clsx from "clsx";
import { Check, RotateCcw } from "lucide-react";
import { useMemo } from "react";

import { Dialog } from "../../components/Dialog";
import { Button, SectionLabel } from "../../components/ui";
import { t } from "../../i18n/i18n";
import { diffWords, sideParts } from "./textDiff";

/** Comparaison d'un paragraphe « à revoir » (docs/18 §6.7) : « Votre texte » et « Texte généré à jour » côte à côte
 *  (empilés sur un écran étroit), écarts surlignés mot à mot — retiré dans votre texte, ajouté dans le texte généré.
 *  « Reprendre le texte généré » remplace votre modification ; « Garder mon texte » retire le signalement.
 *  Aussi « Avant / après » d'un paragraphe mis à jour d'après le dossier (`labels`, en lecture seule). */
export function CompareDialog({
  title,
  mine,
  generated,
  busy,
  readOnly,
  subtitle,
  labels,
  onTakeGenerated,
  onKeepMine,
  onClose,
}: {
  title: string;
  mine: string;
  generated: string;
  busy: boolean;
  readOnly: boolean;
  /** Ce qui a changé le texte généré, à la place de celui de l'ordre (fiche pays : ennemi, ordre, langue…). */
  subtitle?: string;
  /** Titre et colonnes propres (« Avant / après : … », « Texte d'avant », « Texte à jour »). */
  labels?: { title: string; before: string; after: string };
  onTakeGenerated: () => void;
  onKeepMine: () => void;
  onClose: () => void;
}) {
  const parts = useMemo(() => diffWords(mine, generated), [mine, generated]);
  const column = (side: "before" | "after", label: string) => (
    <div className="min-w-0">
      <SectionLabel inset>{label}</SectionLabel>
      <div
        className={clsx(
          "mt-1 max-h-[50vh] overflow-y-auto rounded-lg border border-line bg-canvas px-2.5 py-2",
          "text-ui leading-relaxed whitespace-pre-wrap text-ink",
        )}
      >
        {sideParts(parts, side).map((part, i) => (
          <span
            key={i}
            className={clsx(
              part.kind === "del" && "rounded-sm bg-danger-soft text-danger line-through decoration-danger/50",
              part.kind === "add" && "rounded-sm bg-ok-soft text-ok",
            )}
          >
            {part.text}
          </span>
        ))}
        {!(side === "before" ? mine : generated) && <span className="text-ink-muted">{t("(vide)")}</span>}
      </div>
    </div>
  );
  return (
    <Dialog
      title={labels?.title ?? t("Comparer : {field}", { field: title })}
      subtitle={subtitle ?? t("Le texte généré a changé depuis votre modification (composition, rôles, Données initiales…).")}
      onClose={onClose}
      width={920}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("Fermer")}
          </Button>
          {!readOnly && (
            <>
              <Button variant="secondary" onClick={onKeepMine} disabled={busy}>
                <Check size={14} /> {t("Garder mon texte")}
              </Button>
              <Button onClick={onTakeGenerated} disabled={busy}>
                <RotateCcw size={14} /> {t("Reprendre le texte généré")}
              </Button>
            </>
          )}
        </>
      }
    >
      <div className="grid gap-3 px-4 py-4 md:grid-cols-2">
        {column("before", labels?.before ?? t("Votre texte"))}
        {column("after", labels?.after ?? t("Texte généré à jour"))}
      </div>
    </Dialog>
  );
}
